import { DataValue } from './DataValue';
import { Vector } from '../helpers/Vector';
import { DataType } from './DataType';
import { IntegrationAccess } from './Integration';
import type { NodeExecutionResult } from './Node';

export type UserDefinedType = { kind: 'union'; selectedTypeIndex: number } | { kind: 'customType'; type: DataType };

export interface NodeInstanceDataPort {
    name: string;
    type: DataType;
    optional?: boolean;
}

export type NodeInstanceState = { [key: string]: DataValue };

export interface SingleNodeInstance {
    id: number;
    nodeId: string;
    nodeVersion?: number;
    position: Vector;
    selected: boolean;
    selectedIntegrationAccess?: IntegrationAccess;
    portValues: { [key: string]: DataValue };
    inPorts: NodeInstanceDataPort[];
    outPorts: NodeInstanceDataPort[];
    portUserDefinedTypes: { [key: string]: UserDefinedType };
    width?: number;
    height?: number;
    state: NodeInstanceState;

    selectedVariable?: string;
}

export type NodeInstance = SingleNodeInstance | FunctionNodeInstance;

export type ConnectionPortType = 'external' | 'inherent';

// --- Execution & Validation types ---

export interface ExecutionError {
    nodeInstanceId: number;
    error: string;
}

/** Graph-level execution result: maps nodeInstanceId → per-node result */
export type ExecutionResult = { [nodeInstanceId: number]: NodeExecutionResult };

export interface ConnectionValidation {
    /** Matches DataConnection.id */
    id: number;
    valid: boolean;
    errorMessage?: string;
}

export interface ValidationResult {
    connectionValidations: ConnectionValidation[];
}

export interface DataConnection {
    id: number;

    receivingNodeInstanceId: number;
    receivingPortName: string;
    receivingPortType: ConnectionPortType;

    sendingNodeInstanceId: number;
    sendingPortName: string;
    sendingPortType: ConnectionPortType;
}

export type VariableTypeDetermination = 'INFERRED' | 'FIXED';

export interface GlueVariable {
    name: string;
    typeDetermination: VariableTypeDetermination;
    type?: DataType;
    resolvedType?: DataType;
}

export interface FunctionPortDefinition {
    id: string;
    name: string;
    type: DataType;
    optional?: boolean;
    description?: string;
}

export interface FunctionDefinition {
    id: string;
    name: string;
    description?: string;

    nodeInstances: NodeInstance[];
    connections: DataConnection[];

    inputPortDefinitions: FunctionPortDefinition[];
    outputPortDefinitions: FunctionPortDefinition[];

    functionInputsNodeId: number; // References to the "Function Inputs" node instance
    functionOutputsNodeId: number; // References to the "Function Outputs" node instance

    createdAt: Date;
    updatedAt: Date;
    version: number;
}

export interface FunctionNodeInstance extends SingleNodeInstance {
    functionId: string;
}

export function isFunctionNodeInstance(nodeInstance: NodeInstance): nodeInstance is FunctionNodeInstance {
    return 'functionId' in nodeInstance && !!nodeInstance.functionId;
}

export interface DataGlueGraph {
    nodeInstances: NodeInstance[];
    connections: DataConnection[];
    variables: GlueVariable[];
    functions: FunctionDefinition[]; // Graph-local function registry
    schemaVersion?: number;
}
